import { createRouter, createWebHistory } from 'vue-router'
import appAnalytics from '@/utils/tracking'
import HomeView from '@/views/HomeView.vue'
import GetStartedView from '@/views/GetStartedView.vue'
import RateConnectPageView from '@/views/RateConnectPageView.vue'
import StudioPlusPageView from '@/views/StudioPlusPageView.vue'
import ImprintView from '@/views/ImprintView.vue'
import TermsAndConditionsView from '@/views/TermsAndConditionsView.vue'
import PublicPageLayout from '@/layouts/PublicPageLayout.vue'
import MainPageLayout from '@/layouts/MainPageLayout.vue'
import LoginView from '@/views/auth/LoginView.vue'
import SignupView from '@/views/auth/SignupView.vue'
import InviteView from '@/views/auth/InviteView.vue'
import OnboardingView from '@/views/OnboardingView.vue'
import OrganisationsView from '@/views/OrganisationsView.vue'
import PropertyAssignmentAddNewView from '@/views/property-assignment/PropertyAssignmentAddNewView.vue'
import {
  PropertyDetailsView,
  PropertyDetailsBasicInformation,
  PropertyDetailsImages,
  PropertyDetailsDescription,
} from '@/views/property-details'
import { SettingsView, TeamSettingsView, ProfileSettingsView, LocaleSettingsView } from '@/views/settings'
import {
  RateConnectRateCheckView,
  RateConnectView,
  RateConnectNoCampaignView,
  RateConnectPerformanceView,
  RateConnectNotEligibleView,
  RateConnectNotEligibleReadMoreView,
  RateConnectRunningView,
  RateConnectMarketsView,
  RateConnectBiddingGoalsView,
  RateConnectSetupView,
  RateConnectBudgetView,
  RateConnectOpportunityView,
  RateConnectSmartBoostView,
  RateConnectSmartBoostHistoryView,
  RateConnectBookingDetailsView,
} from '@/views/rate-connect'
import { RateInsightsView, RateInsightsOnboarding, RateInsightsChannels, RateInsights } from '@/views/rate-insights'
import { CompareFeaturesView, StudioPlusPaymentView, StudioPlusView, StudioPlusInsightsView } from '@/views/studio-plus'
import AdminToolView from '@/views/admin-tool/AdminToolView.vue'
import HotelOffersView from '@/views/hotel-offers/HotelOffersView.vue'
import {
  VisitorsProfileView,
  VisitorsProfileStayDurationView,
  VisitorsProfileSearchCountriesView,
  VisitorsProfileSearchWindowView,
} from '@/views/visitors-profile'
import NotAnAdminView from '@/views/NotAdminView.vue'
import {
  PaymentDetailsEditView,
  PremiumProductsView,
  InvoicesView,
  PaymentMethodsView,
  BillingView,
} from '@/views/billing'

import isAuthenticated, { extractMarketingLinkIds } from './middleware/isAuthenticated'
import isAdminToolAuthority from './middleware/isAdminToolAuthority'
import isAdmin from './middleware/isAdmin'
import isDesktopOnly from './middleware/isDesktopOnly'
import OnboardingPageLayout from '@/layouts/OnboardingPageLayout.vue'

import { selectedAccommodationId } from '@/layouts/queries'
import { storageSession } from '@/utils/storageService'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      beforeEnter: isAuthenticated,
      meta: {
        layout: MainPageLayout,
        isFullWidth: true,
      },
    },
    {
      path: '/onboarding',
      name: 'onboarding',
      component: OnboardingView,
      beforeEnter: isAuthenticated,
      meta: {
        layout: OnboardingPageLayout,
      },
    },
    {
      path: '/organisations',
      name: 'organisations',
      component: OrganisationsView,
      beforeEnter: isAuthenticated,
      meta: {
        layout: MainPageLayout,
      },
    },
    {
      path: '/property-assignment',
      name: 'property-assignment',
      component: PropertyAssignmentAddNewView,
      beforeEnter: isAuthenticated,
      meta: {
        layout: MainPageLayout,
      },
    },
    {
      path: '/hotel-offers',
      name: 'hotel-offers',
      component: HotelOffersView,
      beforeEnter: [isAuthenticated, isDesktopOnly],
      meta: {
        layout: MainPageLayout,
        isFullWidth: true,
        requires: { hasStudioPlus: true },
      },
    },
    {
      path: '/rate-insights',
      name: 'rate-insights',
      component: RateInsights,
      beforeEnter: [isAuthenticated, isDesktopOnly],
      meta: {
        layout: MainPageLayout,
        requires: { hasStudioPlus: true },
      },
      children: [
        {
          path: 'view',
          name: 'rate-insights-view',
          component: RateInsightsView,
          meta: { requires: { hasStudioPlus: true }, isFullWidth: true },
        },
        {
          path: 'onboarding',
          name: 'rate-insights-onboarding',
          component: RateInsightsOnboarding,
          meta: { requires: { hasStudioPlus: true }, isFullWidth: true },
        },
        {
          path: 'update',
          name: 'rate-insights-update',
          component: RateInsightsOnboarding,
          meta: { requires: { hasStudioPlus: true }, isFullWidth: true },
        },
        {
          path: 'channels',
          name: 'rate-insights-channels',
          component: RateInsightsChannels,
          meta: { requires: { hasStudioPlus: true } },
        },
      ],
    },
    {
      path: '/visitors-profile',
      name: 'visitors-profile',
      component: VisitorsProfileView,
      beforeEnter: [isAuthenticated, isDesktopOnly],
      redirect: { name: 'visitors-profile-stay-duration' },
      meta: {
        layout: MainPageLayout,
        requires: { hasStudioPlus: true },
      },
      children: [
        {
          path: 'stay-duration',
          name: 'visitors-profile-stay-duration',
          component: VisitorsProfileStayDurationView,
          meta: { requires: { hasStudioPlus: true } },
        },
        {
          path: 'search-window',
          name: 'visitors-profile-search-window',
          component: VisitorsProfileSearchWindowView,
          meta: { requires: { hasStudioPlus: true } },
        },
        {
          path: 'search-countries',
          name: 'visitors-profile-search-countries',
          component: VisitorsProfileSearchCountriesView,
          meta: { requires: { hasStudioPlus: true } },
        },
      ],
    },
    {
      path: '/rate-connect',
      name: 'rate-connect',
      component: RateConnectView,
      beforeEnter: isAuthenticated,
      meta: {
        layout: MainPageLayout,
        isFullWidth: true,
      },
      children: [
        {
          path: '',
          name: 'rate-connect-index',
          redirect: { name: 'rate-connect-running-performance' },
        },
        {
          path: 'no-campaign',
          name: 'rate-connect-no-campaign',
          component: RateConnectNoCampaignView,
          meta: {
            requires: {
              hasRateConnect: false,
              isEligible: true,
            },
          },
        },
        {
          path: 'not-eligible',
          name: 'rate-connect-not-eligible',
          component: RateConnectNotEligibleView,
          meta: {
            requires: {
              hasRateConnect: false,
              isEligible: false,
            },
          },
        },
        {
          path: 'not-eligible-read-more',
          name: 'rate-connect-not-eligible-read-more',
          component: RateConnectNotEligibleReadMoreView,
          meta: {
            requires: {
              hasRateConnect: false,
              isEligible: false,
            },
          },
        },
        {
          path: 'setup',
          name: 'rate-connect-setup',
          component: RateConnectSetupView,
          meta: {
            layout: MainPageLayout,
            isFullWidth: true,
            requires: {
              userRole: 'admin',
              hasRateConnect: false,
              isEligible: true,
            },
          },
        },
        {
          name: 'rate-connect-rate-check',
          path: 'rate-check',
          component: RateConnectRateCheckView,
          meta: {
            layout: MainPageLayout,
            isFullWidth: true,
          },
        },
        {
          path: 'running',
          name: 'rate-connect-running',
          component: RateConnectRunningView,
          redirect: to => ({
            name: 'rate-connect-running-performance',
            query: to.query,
            params: to.params,
          }),
          meta: {
            layout: MainPageLayout,
            isFullWidth: true,
            requires: {
              hasRateConnect: true,
            },
          },
          children: [
            {
              path: 'performance',
              name: 'rate-connect-running-performance',
              component: RateConnectPerformanceView,
              meta: {
                requires: {
                  hasRateConnect: true,
                },
              },
            },
            {
              path: 'booking-details',
              name: 'rate-connect-running-booking-details',
              component: RateConnectBookingDetailsView,
              meta: {
                requires: {
                  hasRateConnect: true,
                },
              },
            },
            {
              path: 'opportunity',
              name: 'rate-connect-running-opportunity',
              component: RateConnectOpportunityView,
              meta: {
                requires: {
                  hasRateConnect: true,
                  campaignType: 'netcpa',
                },
              },
            },
            {
              path: 'smart-boost',
              name: 'rate-connect-running-smart-boost',
              component: RateConnectSmartBoostView,
              meta: {
                requires: {
                  hasRateConnect: true,
                  campaignType: 'netcpa',
                },
              },
            },
            {
              path: 'smart-boost-history',
              name: 'rate-connect-running-smart-boost-history',
              component: RateConnectSmartBoostHistoryView,
              meta: {
                requires: {
                  hasRateConnect: true,
                  campaignType: 'netcpa',
                },
              },
            },
            {
              path: 'markets',
              name: 'rate-connect-running-markets',
              component: RateConnectMarketsView,
              meta: {
                requires: {
                  hasRateConnect: true,
                },
              },
            },
            {
              path: 'bidding',
              name: 'rate-connect-running-bidding',
              component: RateConnectBiddingGoalsView,
              meta: {
                requires: {
                  hasRateConnect: true,
                  campaignType: 'cpc',
                },
              },
            },

            {
              path: 'budget',
              name: 'rate-connect-running-budget',
              component: RateConnectBudgetView,
              meta: {
                requires: {
                  hasRateConnect: true,
                  campaignType: 'cpc',
                },
              },
            },
          ],
        },
      ],
    },
    {
      path: '/billing',
      name: 'billing',
      component: BillingView,
      beforeEnter: isAuthenticated,
      meta: {
        layout: MainPageLayout,
        isFullWidth: true,
      },
      children: [
        {
          path: 'premium-products',
          name: 'billing-premium-products',
          component: PremiumProductsView,
        },
        {
          path: 'invoices',
          name: 'billing-invoices',
          component: InvoicesView,
        },
        {
          path: 'payment-methods',
          name: 'billing-payment-methods',
          component: PaymentMethodsView,
        },
        {
          path: 'payment-methods/edit-payment',
          name: 'billing-edit-payment',
          beforeEnter: isAdmin,
          component: PaymentDetailsEditView,
        },
      ],
    },
    {
      path: '/settings',
      component: SettingsView,
      beforeEnter: isAuthenticated,
      meta: {
        layout: MainPageLayout,
        isFullWidth: true,
      },
      redirect: { name: 'settings-team-settings' },
      children: [
        {
          path: 'team-settings',
          name: 'settings-team-settings',
          beforeEnter: isAdmin,
          component: TeamSettingsView,
        },
        {
          path: 'profile-settings',
          name: 'settings-profile-settings',
          component: ProfileSettingsView,
        },
        {
          path: 'locale-settings',
          name: 'settings-locale-settings',
          component: LocaleSettingsView,
        },
      ],
    },
    {
      path: '/property-details',
      name: 'property-details',
      component: PropertyDetailsView,
      beforeEnter: isAuthenticated,
      meta: {
        layout: MainPageLayout,
      },
      children: [
        {
          path: 'basic-information',
          name: 'property-details-basic-information',
          component: PropertyDetailsBasicInformation,
        },
        {
          path: 'images',
          name: 'property-details-images',
          component: PropertyDetailsImages,
        },
        {
          path: 'description',
          name: 'property-details-description',
          component: PropertyDetailsDescription,
        },
      ],
    },
    {
      path: '/studio-plus',
      name: 'studio-plus',
      beforeEnter: isAuthenticated,
      component: StudioPlusView,
      meta: {
        layout: MainPageLayout,
      },
      children: [
        {
          path: 'compare',
          name: 'studio-plus-compare',
          component: CompareFeaturesView,
          meta: {
            layout: MainPageLayout,
            isFullWidth: true,
            requires: { hasStudioPlus: false, isEligible: true },
          },
        },
        {
          path: 'payment',
          name: 'studio-plus-payment',
          component: StudioPlusPaymentView,
          meta: {
            layout: MainPageLayout,
            isFullWidth: true,
            requires: { hasStudioPlus: false, isEligible: true, userRole: 'admin' },
          },
        },
      ],
    },
    {
      path: '/studio-plus-insights',
      name: 'studio-plus-insights',
      beforeEnter: isAuthenticated,
      component: StudioPlusInsightsView,
      meta: {
        layout: MainPageLayout,
        isFullWidth: true,
        requires: { hasStudioPlus: true },
      },
    },
    {
      path: '/admin-tool',
      name: 'admin-tool',
      component: AdminToolView,
      beforeEnter: isAdminToolAuthority,
      meta: {
        layout: MainPageLayout,
      },
    },
    {
      path: '/not-an-admin',
      name: 'not-admin-error',
      component: NotAnAdminView,
      meta: {
        layout: MainPageLayout,
      },
    },
    {
      path: '/getstarted',
      name: 'getstarted',
      component: GetStartedView,
    },
    {
      path: '/getstarted/rate-connect',
      name: 'getstarted-rate-connect',
      component: RateConnectPageView,
    },
    {
      path: '/getstarted/studio-plus',
      name: 'getstarted-studio-plus',
      component: StudioPlusPageView,
    },
    {
      path: '/imprint',
      name: 'imprint',
      component: ImprintView,
      meta: {
        layout: PublicPageLayout,
      },
    },
    {
      path: '/terms-and-conditions',
      name: 'terms-and-conditions',
      component: TermsAndConditionsView,
      meta: {
        layout: PublicPageLayout,
      },
    },
    {
      path: '/auth',
      children: [
        {
          path: 'login',
          name: 'login',
          component: LoginView,
        },
        {
          path: 'invite',
          component: InviteView,
        },
        {
          path: 'signup',
          component: SignupView,
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    // If navigating between child routes, don't scroll
    if (to.matched[0]?.path === from.matched[0]?.path) {
      return {}
    }

    if (savedPosition) {
      return savedPosition
    }

    return { top: 0, left: 0 }
  },
})

router.beforeEach((to, from, next) => {
  if (import.meta.env.MODE === 'production') {
    appAnalytics.page({
      to: to.fullPath,
      title: to.name,
      accommodationId: selectedAccommodationId.value ?? null,
    })
  }
  if (to.query?.['utm_medium']) {
    window.sessionStorage?.setItem?.('traffic_source', String(to.query['utm_medium']))
  }
  const voucherCode = to.query?.['voucherCode']
  if (voucherCode) {
    const { organisationId, accommodationId } = extractMarketingLinkIds(to.query)
    window.sessionStorage.removeItem('rc_voucher_pending_code')
    window.sessionStorage.removeItem('rc_voucher_pending_item_id')
    if (accommodationId && organisationId) {
      window.sessionStorage.setItem('rc_voucher_pending_code', String(voucherCode))
      window.sessionStorage.setItem('rc_voucher_pending_item_id', accommodationId)
    }
  }

  const isRateConnectPath = path => path?.startsWith('/rate-connect')
  const wasOnRateConnect = isRateConnectPath(from.path)
  const isGoingToRateConnect = isRateConnectPath(to.path)

  if (wasOnRateConnect && !isGoingToRateConnect) {
    storageSession.removeItem('new_campaign_created')
  }

  next()
})

export default router
